import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import TextField from './../textfield/'
import BaseTextField from '@material-ui/core/TextField'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import dayjs from 'dayjs'
import InputAdornment from '@material-ui/core/InputAdornment'
import DateRangeIcon from '@material-ui/icons/DateRange'
import { useTranslation } from 'react-i18next'
import isBetween from 'dayjs/plugin/isBetween'
import Note from './../note/'
import List from '@material-ui/core/List'
import ListSubheader from '@material-ui/core/ListSubheader'
import ListItemText from '@material-ui/core/ListItemText'
import ListItem from '@material-ui/core/ListItem'
// Material UI date picker using DayJS
import DayJSUtil from '@date-io/dayjs';
import { MuiPickersUtilsProvider, Calendar } from '@material-ui/pickers'

// We require this functionality in order to
// support the "date range" component
dayjs.extend(isBetween)

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: 450
  },
  actions: {
    padding: 0
  },
  between: {
    backgroundColor: theme.palette.background.offset,
    borderRadius: 0
  },
  yieldLeft: {
    borderRadius: '0 50% 50% 0'
  },
  yieldRight: {
    borderRadius: '50% 0 0 50%'
  }
}))

export const DateSelector = ({ value, onDateSelect, TextFieldProps }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  // Default is today
  const [ open, setOpen ] = useState(false)
  const [ current, setCurrent ] = useState(value)

  return (
    <MuiPickersUtilsProvider utils={DayJSUtil}>
      <TextField
        label="Date"
        margin="none"
        value={`${value.format('DD/MM/YY')}`}
        InputProps={{
          endAdornment: <InputAdornment position="end"><DateRangeIcon/></InputAdornment>,
        }}
        onClick={() => setOpen(true)}
        {...TextFieldProps} />

      <Dialog open={open} onClose={() => setOpen(false)}>
        <Box display="flex" overflow="hidden">
          <Box p={2} flexGrow={1}>
            <Box>
              <Calendar
                date={current}
                onChange={(val) => setCurrent(val)} />
            </Box>
            <DialogActions className={classes.actions}>
              <Button color="default" onClick={() => setOpen(false)}>
                {t('cancel')}
              </Button>
              <Button color="primary" autoFocus onClick={() => {
                onDateSelect(current)
                setOpen(false)
              }}>
                {t('apply')}
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </Dialog>
    </MuiPickersUtilsProvider>
  )
}

DateSelector.defaultProps = {
  value: dayjs()
}

/**
 * Construct a date range component. Allows selecting the range inside
 * a dialog component. This picker only supports "dayjs"
 */
const DateRange = ({ onDateRange, initialFrom, initialTo, minDate = null, ...rest }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  // Default is today
  const [ selected, setSelected ] = useState([initialFrom, initialTo])
  const [ open, setOpen ] = useState(false)

  const pushSelected = (val) => {
    selected.unshift(val)
    setSelected(selected.splice(0, 2))
  }

  // Extract the to/from values. We add "concat" so we create a copy
  // of the array instead of mutating the state data
  const [ from, to ] = selected.concat().sort((a, b) => a.unix() - b.unix())

  const diffToday = dayjs().diff(dayjs().subtract(24, 'hours'), 'hours')
  const diffWeek = dayjs().diff(dayjs().subtract(7, 'days').startOf('day'), 'hours')
  const diffMonth = dayjs().diff(dayjs().subtract(30, 'days').startOf('day'), 'hours')
  const diff = to.diff(from.startOf('day'), 'hours')

  return (
    <MuiPickersUtilsProvider utils={DayJSUtil}>
      <BaseTextField
        margin="none"
        value={`${from.format('DD/MM/YY')} - ${to.format('DD/MM/YY')}`}
        InputProps={{
          endAdornment: <InputAdornment position="end"><DateRangeIcon/></InputAdornment>,
        }}
        onClick={() => setOpen(true)}
        {...rest} />

      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: classes.dialog }}>
        <Box display="flex">
          <Box minWidth={120} bgcolor="background.offset">
            <List
              subheader={<ListSubheader>Shortcuts</ListSubheader>}>
              <ListItem dense button selected={diff === diffToday} onClick={() => setSelected([dayjs(), dayjs()])}>
                <ListItemText primary={t('dateRange:today')} />
              </ListItem>
              <ListItem dense button selected={diff === diffWeek} onClick={() => setSelected([dayjs().subtract(7, 'days'), dayjs()])}>
                <ListItemText primary={t('dateRange:lastWeek')} />
              </ListItem>
              <ListItem dense button selected={diff === diffMonth} onClick={() => setSelected([dayjs().subtract(30, 'days'), dayjs()])}>
                <ListItemText primary={t('dateRange:lastMonth')} />
              </ListItem>
            </List>
          </Box>
          <Box p={2} flexGrow={1}>
            <Note>{t('dateRange:info')}</Note>
            <Box>
              <Calendar
                date={to}
                disableFuture
                minDate={minDate || undefined}
                renderDay={(day, selectedDate, isDayInCurrentMonth, dayComponent) => {
                  /* Ignore the selectedDate, it only carries the last clicked item */
                  const component = React.cloneElement(dayComponent, {
                    selected: day.isSame(from, 'day') || day.isSame(to, 'day'),
                    hidden: false,
                    disabled: dayComponent.props.disabled || !isDayInCurrentMonth
                  })

                  return (
                    <Box className={clsx({
                      [classes.between]: day.isBetween(from, to, 'day', '[]') && !to.isSame(from, 'day'),
                      [classes.yieldRight]: day.isSame(from, 'day'),
                      [classes.yieldLeft]: day.isSame(to, 'day')
                    })}>
                      {component}
                    </Box>
                  )
                }}
                onChange={(val) => pushSelected(val)} />
            </Box>
            <DialogActions className={classes.actions}>
              <Button color="default" onClick={() => setOpen(false)}>
                {t('cancel')}
              </Button>
              <Button color="primary" autoFocus onClick={() => {
                onDateRange(from.startOf('day'), to.endOf('day'))
                setOpen(false)
              }}>
                {t('apply')}
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </Dialog>
    </MuiPickersUtilsProvider>
  )
}

DateRange.propTypes = {
  onDateRange: PropTypes.func.isRequired,
  initialFrom: PropTypes.instanceOf(dayjs).isRequired,
  initialTo: PropTypes.instanceOf(dayjs).isRequired
}

export default DateRange
